import { FC, useState } from 'react';
import { Box, Text, Button, VStack } from '@chakra-ui/react';
import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogActionTrigger,
} from '@/components/ui/dialog';
import { Dog } from '@/types/dog';

interface DeleteDogDialogProps {
  dog: Dog;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDelete: (permanent: boolean) => Promise<void>;
  isDeleting: boolean;
}

/**
 * 個体削除確認ダイアログコンポーネント
 * 論理削除（ステータス変更）と物理削除（完全削除）を選択可能
 */
export const DeleteDogDialog: FC<DeleteDogDialogProps> = ({
  dog,
  open,
  onOpenChange,
  onDelete,
  isDeleting,
}) => {
  const [deleteMode, setDeleteMode] = useState<'logical' | 'permanent'>('logical');

  const handleDelete = async () => {
    await onDelete(deleteMode === 'permanent');
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <DialogRoot open={open} onOpenChange={(e) => onOpenChange(e.open)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>個体の削除</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text fontWeight="medium">「{dog.name}」を削除しますか？</Text>

            {/* 削除方法選択 */}
            <VStack align="stretch" gap={3}>
              {/* 論理削除オプション */}
              <Box
                as="label"
                p={3}
                borderRadius="md"
                borderWidth={2}
                borderColor={deleteMode === 'logical' ? 'blue.500' : 'gray.200'}
                bg={deleteMode === 'logical' ? 'blue.50' : 'white'}
                cursor="pointer"
                _hover={{ borderColor: deleteMode === 'logical' ? 'blue.500' : 'gray.300' }}
              >
                <Box display="flex" alignItems="flex-start" gap={3}>
                  <input
                    type="radio"
                    name="deleteMode"
                    value="logical"
                    checked={deleteMode === 'logical'}
                    onChange={() => setDeleteMode('logical')}
                    style={{ marginTop: '4px' }}
                    aria-label="ステータスを「死亡」に変更（推奨）"
                  />
                  <Box flex={1}>
                    <Text fontWeight="medium" fontSize="sm">
                      ステータスを「死亡」に変更（推奨）
                    </Text>
                    <Text fontSize="xs" color="gray.600" mt={1}>
                      データは保持され、履歴から参照可能
                    </Text>
                  </Box>
                </Box>
              </Box>

              {/* 物理削除オプション */}
              <Box
                as="label"
                p={3}
                borderRadius="md"
                borderWidth={2}
                borderColor={deleteMode === 'permanent' ? 'red.500' : 'gray.200'}
                bg={deleteMode === 'permanent' ? 'red.50' : 'white'}
                cursor="pointer"
                _hover={{ borderColor: deleteMode === 'permanent' ? 'red.500' : 'gray.300' }}
              >
                <Box display="flex" alignItems="flex-start" gap={3}>
                  <input
                    type="radio"
                    name="deleteMode"
                    value="permanent"
                    checked={deleteMode === 'permanent'}
                    onChange={() => setDeleteMode('permanent')}
                    style={{ marginTop: '4px' }}
                    aria-label="完全に削除"
                  />
                  <Box flex={1}>
                    <Text fontWeight="medium" fontSize="sm">
                      完全に削除
                    </Text>
                    <Text fontSize="xs" color="red.600" mt={1}>
                      この操作は取り消せません
                    </Text>
                  </Box>
                </Box>
              </Box>
            </VStack>
          </VStack>
        </DialogBody>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <Button variant="outline" onClick={handleCancel}>
              キャンセル
            </Button>
          </DialogActionTrigger>
          <Button
            colorPalette="red"
            onClick={handleDelete}
            disabled={isDeleting}
          >
            {isDeleting ? '削除中...' : '削除'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
